import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import oauthApi from '../../../services/oauthApi';
import { OAuthProviderType } from '../../../data/enums';

// Module-level cache of the resolved boolean, keyed by company id, so
// repeat mounts across Menus/Inventory/Import within the same page session
// don't refetch. Invalidation isn't handled here: connecting/reconnecting/
// disconnecting Square in practice happens through a flow that ends in a
// full page navigation (OAuth redirect, settings save + redirect), which
// resets this module-level cache anyway.
const integratedCache = new Map();

// Company-level "has at least one Square-integrated store" signal, used by
// the pages that host relocated Square tools (menu editor, inventory, import).
// Backed by a DB-only endpoint (no live Square call, no token refresh) so
// it's safe to call on every page view, including for companies with no
// Square integration at all.
const useSquareIntegrated = () => {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const companyId = globalCompany?.id;
  const [integrated, setIntegrated] = useState(
    () => (companyId && integratedCache.has(companyId) ? integratedCache.get(companyId) : false)
  );
  const [checked, setChecked] = useState(() => !!(companyId && integratedCache.has(companyId)));

  useEffect(() => {
    if (!companyId) {
      setIntegrated(false);
      setChecked(false);
      return undefined;
    }

    if (integratedCache.has(companyId)) {
      setIntegrated(integratedCache.get(companyId));
      setChecked(true);
      return undefined;
    }

    let cancelled = false;
    setIntegrated(false);
    setChecked(false);
    (async () => {
      try {
        const res = await oauthApi.hasIntegratedStore({
          CompanyId: companyId,
          Type: OAuthProviderType.Square,
        });
        const value = !!res?.data?.data;
        if (!cancelled) {
          integratedCache.set(companyId, value);
          setIntegrated(value);
        }
      } catch (e) {
        if (!cancelled) setIntegrated(false);
      } finally {
        if (!cancelled) setChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  return { integrated, checked };
};

export default useSquareIntegrated;
